import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, Edit3, Star, X, ExternalLink, Youtube, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImagePicker, { absolutizeUrl } from "@/components/ImagePicker";

const EMPTY_TRAIL = {
    slug: "",
    name: "",
    summary: "",
    description: "",
    difficulty: "red",
    terrain: "misto",
    length_km: 0,
    elevation_m: 0,
    tips: "",
    image_url: "",
    youtube_url: "",
    trailforks_url: "",
    note_fondo: "",
    is_trail_of_month: false,
};

function slugify(s) {
    return (s || "")
        .toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export default function Admin() {
    const { user, checking } = useAuth();
    const nav = useNavigate();
    const [trails, setTrails] = useState([]);
    const [editing, setEditing] = useState(null); // null = closed, {} or trail obj

    useEffect(() => {
        if (!checking && (!user || user.role !== "admin")) {
            nav("/login");
        }
    }, [user, checking, nav]);

    const load = async () => {
        try {
            const { data } = await api.get("/trails");
            setTrails(data);
        } catch {
            setTrails([]);
        }
    };
    useEffect(() => { load(); }, []);

    const startNew = () => setEditing({ ...EMPTY_TRAIL });
    const startEdit = (t) => setEditing({ ...t });
    const close = () => setEditing(null);

    const save = async (e) => {
        e.preventDefault();
        const payload = {
            ...editing,
            slug: editing.slug || slugify(editing.name),
            length_km: parseFloat(editing.length_km) || 0,
            elevation_m: parseInt(editing.elevation_m, 10) || 0,
            youtube_url: editing.youtube_url || null,
            trailforks_url: editing.trailforks_url || null,
            komoot_url: editing.komoot_url || null,
            google_maps_url: editing.google_maps_url || null,
            state: editing.state || "open",
            condition: editing.condition || "unknown",
            state_note: editing.state_note || "",
            state_note_en: editing.state_note_en || "",
        };
        try {
            if (editing.id) {
                await api.put(`/trails/${editing.id}`, payload);
                toast.success("Trail aggiornato");
            } else {
                await api.post("/trails", payload);
                toast.success("Trail pubblicato");
            }
            close();
            load();
        } catch (e) {
            const msg = e.response?.data?.detail || "Errore";
            toast.error(typeof msg === "string" ? msg : "Errore di validazione");
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Eliminare "${t.name}"?`)) return;
        try {
            await api.delete(`/trails/${t.id}`);
            toast.success("Trail eliminato");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        }
    };

    const toggleFeatured = async (t) => {
        try {
            await api.put(`/trails/${t.id}`, { ...t, is_trail_of_month: !t.is_trail_of_month });
            toast.success(!t.is_trail_of_month ? "Impostato come trail del mese" : "Rimosso dal trail del mese");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        }
    };

    if (checking) return <div className="p-20 text-zinc-500">…</div>;
    if (!user || user.role !== "admin") return null;

    return (
        <div data-testid="admin-page" className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-abnormal-red font-bold mb-3">ADMIN</p>
                    <h1 className="font-display text-5xl md:text-7xl uppercase leading-none">Gestione trail</h1>
                    <p className="text-zinc-400 mt-4 max-w-2xl">
                        Aggiungi, modifica, elimina. Incolla qui sotto il link Trailforks e il video YouTube.
                    </p>
                </div>
                <button
                    data-testid="admin-add-trail-btn"
                    onClick={startNew}
                    className="bg-abnormal-red text-white uppercase tracking-wider text-sm font-bold px-5 py-3 inline-flex items-center gap-2 hover:bg-white hover:text-abnormal-red transition-colors"
                >
                    <Plus size={16} /> Nuovo trail
                </button>
            </div>

            {/* Quick links to other admin areas */}
            <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/admin/stats" data-testid="admin-stats-link" className="text-xs uppercase tracking-[0.25em] font-bold border border-white/20 px-4 py-2 hover:border-abnormal-red hover:text-abnormal-red transition-colors">Stats</Link>
                <Link to="/admin/quest" data-testid="admin-quest-link" className="text-xs uppercase tracking-[0.25em] font-bold border border-white/20 px-4 py-2 hover:border-abnormal-red hover:text-abnormal-red transition-colors">Quest</Link>
                <Link to="/admin/moderation" data-testid="admin-moderation-link" className="text-xs uppercase tracking-[0.25em] font-bold border border-abnormal-red text-abnormal-red px-4 py-2 hover:bg-abnormal-red hover:text-white transition-colors">Moderazione</Link>
                <Link to="/admin/social-post" data-testid="admin-social-post-link" className="text-xs uppercase tracking-[0.25em] font-bold border border-abnormal-red text-abnormal-red px-4 py-2 hover:bg-abnormal-red hover:text-white transition-colors">Social Post</Link>
                <Link to="/admin/events" data-testid="admin-events-link" className="text-xs uppercase tracking-[0.25em] font-bold border border-abnormal-red text-abnormal-red px-4 py-2 hover:bg-abnormal-red hover:text-white transition-colors">Eventi</Link>
                <Link to="/admin/guide" data-testid="admin-guides-link" className="text-xs uppercase tracking-[0.25em] font-bold border border-white/20 px-4 py-2 hover:border-abnormal-red hover:text-abnormal-red transition-colors">Guide</Link>
                <Link to="/admin/stay" data-testid="admin-stay-link" className="text-xs uppercase tracking-[0.25em] font-bold border border-abnormal-red text-abnormal-red px-4 py-2 hover:bg-abnormal-red hover:text-white transition-colors">Stay (foto)</Link>
                <Link to="/admin/newsletter" data-testid="admin-newsletter-link" className="text-xs uppercase tracking-[0.25em] font-bold border border-white/20 px-4 py-2 hover:border-abnormal-red hover:text-abnormal-red transition-colors">Newsletter</Link>
                <Link to="/admin/settings" data-testid="admin-settings-link" className="text-xs uppercase tracking-[0.25em] font-bold border border-white/20 px-4 py-2 hover:border-abnormal-red hover:text-abnormal-red transition-colors">Settings</Link>
            </div>

            <div data-testid="admin-trails-list" className="mt-10 border border-white/10">
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-zinc-900 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
                    <div className="col-span-4">Nome</div>
                    <div className="col-span-2">Difficoltà</div>
                    <div className="col-span-2">Terreno</div>
                    <div className="col-span-1">Km</div>
                    <div className="col-span-1">D−</div>
                    <div className="col-span-2 text-right">Azioni</div>
                </div>
                {trails.length === 0 && (
                    <div className="p-10 text-zinc-500 text-center">Nessun trail. Aggiungi il primo.</div>
                )}
                {trails.map((t) => (
                    <div key={t.id} className="grid md:grid-cols-12 gap-4 px-5 py-4 border-t border-white/10 items-center hover:bg-white/5">
                        <div className="col-span-4 flex items-center gap-3">
                            <img src={absolutizeUrl(t.image_url)} alt="" className="w-14 h-14 object-cover border border-white/10" />
                            <div className="min-w-0">
                                <p className="font-display text-xl uppercase leading-none flex items-center gap-2">
                                    {t.name}
                                    {t.is_trail_of_month && <span className="text-abnormal-red text-[10px] tracking-[0.2em] uppercase">★ DEL MESE</span>}
                                </p>
                                <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1 truncate">/{t.slug}</p>
                            </div>
                        </div>
                        <div className="col-span-2 uppercase text-sm">{t.difficulty}</div>
                        <div className="col-span-2 uppercase text-sm">{t.terrain}</div>
                        <div className="col-span-1 font-display text-2xl">{t.length_km}</div>
                        <div className="col-span-1 font-display text-2xl">{t.elevation_m}</div>
                        <div className="col-span-2 flex justify-end gap-2">
                            <button
                                data-testid={`admin-feature-${t.slug}`}
                                onClick={() => toggleFeatured(t)}
                                title="Trail del mese"
                                className={`p-2 border transition-colors ${t.is_trail_of_month ? "bg-abnormal-red border-abnormal-red text-white" : "border-white/20 text-zinc-400 hover:border-abnormal-red hover:text-abnormal-red"}`}
                            >
                                <Star size={14} />
                            </button>
                            <button
                                data-testid={`admin-edit-${t.slug}`}
                                onClick={() => startEdit(t)}
                                className="p-2 border border-white/20 text-zinc-400 hover:border-white hover:text-white"
                            >
                                <Edit3 size={14} />
                            </button>
                            <button
                                data-testid={`admin-delete-${t.slug}`}
                                onClick={() => remove(t)}
                                className="p-2 border border-white/20 text-zinc-400 hover:border-abnormal-red hover:text-abnormal-red"
                            >
                                <Trash2 size={14} />
                            </button>
                            <Link
                                to={`/trail/${t.slug}`}
                                target="_blank"
                                className="p-2 border border-white/20 text-zinc-400 hover:border-white hover:text-white"
                                title="Apri"
                            >
                                <ExternalLink size={14} />
                            </Link>
                        </div>
                    </div>
                ))}
            </div>

            {/* EDIT DRAWER */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={close}>
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={save}
                        data-testid="admin-trail-form"
                        className="bg-zinc-950 border border-white/10 w-full max-w-3xl p-6 md:p-10 space-y-5 my-10"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-xs tracking-[0.3em] uppercase text-abnormal-red font-bold">{editing.id ? "MODIFICA TRAIL" : "NUOVO TRAIL"}</p>
                                <h2 className="font-display text-4xl uppercase mt-1">{editing.name || "Senza nome"}</h2>
                            </div>
                            <button type="button" onClick={close} className="text-zinc-400 hover:text-white"><X /></button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Nome">
                                <input
                                    required
                                    data-testid="form-name"
                                    value={editing.name}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Slug (URL)" hint="auto-generato dal nome se vuoto">
                                <input
                                    data-testid="form-slug"
                                    value={editing.slug}
                                    onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
                                    placeholder={slugify(editing.name)}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Summary (1 riga)" full>
                                <input
                                    data-testid="form-summary"
                                    value={editing.summary}
                                    onChange={(e) => setEditing({ ...editing, summary: e.target.value })}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Descrizione" full>
                                <textarea
                                    rows={4}
                                    data-testid="form-description"
                                    value={editing.description}
                                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                    className={`${inputCls} min-h-[100px]`}
                                />
                            </Field>
                            <Field label="Difficoltà">
                                <select
                                    data-testid="form-difficulty"
                                    value={editing.difficulty}
                                    onChange={(e) => setEditing({ ...editing, difficulty: e.target.value })}
                                    className={inputCls}
                                >
                                    <option value="blue">Blue (Trailforks)</option>
                                    <option value="red">Red (Trailforks)</option>
                                    <option value="black">Black (Trailforks)</option>
                                </select>
                            </Field>
                            <Field label="Terreno">
                                <select
                                    data-testid="form-terrain"
                                    value={editing.terrain}
                                    onChange={(e) => setEditing({ ...editing, terrain: e.target.value })}
                                    className={inputCls}
                                >
                                    <option value="roccia">Roccia</option>
                                    <option value="flow">Flow</option>
                                    <option value="tecnico">Tecnico</option>
                                    <option value="misto">Misto</option>
                                </select>
                            </Field>
                            <Field label="Lunghezza (km)">
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    data-testid="form-length"
                                    value={editing.length_km}
                                    onChange={(e) => setEditing({ ...editing, length_km: e.target.value })}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Dislivello D− (m, discesa)">
                                <input
                                    type="number"
                                    min="0"
                                    data-testid="form-elevation"
                                    value={editing.elevation_m}
                                    onChange={(e) => setEditing({ ...editing, elevation_m: e.target.value })}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Consigli pratici (tips)" full>
                                <textarea
                                    rows={3}
                                    data-testid="form-tips"
                                    value={editing.tips}
                                    onChange={(e) => setEditing({ ...editing, tips: e.target.value })}
                                    className={`${inputCls} min-h-[80px]`}
                                />
                            </Field>
                            <Field label="Note sul fondo" full hint="Rami, pietre, frane, fondo rovinato, lavori, zone da evitare.">
                                <textarea
                                    rows={3}
                                    data-testid="form-note-fondo"
                                    value={editing.note_fondo || ""}
                                    onChange={(e) => setEditing({ ...editing, note_fondo: e.target.value })}
                                    placeholder="Es: ramo caduto a metà discesa, sezione roccia in alto bagnata dopo pioggia, attenzione frana nel canale finale…"
                                    className={`${inputCls} min-h-[80px]`}
                                />
                            </Field>
                            <Field label="Copertina trail" full>
                                <ImagePicker
                                    value={editing.image_url}
                                    onChange={(u) => setEditing({ ...editing, image_url: u })}
                                    testidPrefix="form-image"
                                    placeholder="https://… oppure carica foto"
                                />
                            </Field>
                            <Field label="Link YouTube" icon={<Youtube size={12} />} hint="https://youtube.com/watch?v=… o /embed/…">
                                <input
                                    type="url"
                                    data-testid="form-youtube"
                                    value={editing.youtube_url || ""}
                                    onChange={(e) => setEditing({ ...editing, youtube_url: e.target.value })}
                                    placeholder="https://www.youtube.com/watch?v=…"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Link Trailforks" icon={<MapPin size={12} />} hint="incolla la URL del trail su trailforks.com">
                                <input
                                    type="url"
                                    data-testid="form-trailforks"
                                    value={editing.trailforks_url || ""}
                                    onChange={(e) => setEditing({ ...editing, trailforks_url: e.target.value })}
                                    placeholder="https://www.trailforks.com/trails/…"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Link Komoot" icon={<MapPin size={12} />} hint="URL del tour su komoot.com (visibile come bottone in scheda)">
                                <input
                                    type="url"
                                    data-testid="form-komoot"
                                    value={editing.komoot_url || ""}
                                    onChange={(e) => setEditing({ ...editing, komoot_url: e.target.value })}
                                    placeholder="https://www.komoot.com/tour/…"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Link Google Maps" icon={<MapPin size={12} />} hint="URL su maps.google.com">
                                <input
                                    type="url"
                                    data-testid="form-gmaps"
                                    value={editing.google_maps_url || ""}
                                    onChange={(e) => setEditing({ ...editing, google_maps_url: e.target.value })}
                                    placeholder="https://maps.google.com/…"
                                    className={inputCls}
                                />
                            </Field>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 pt-3 border-t border-white/10">
                            <Field label="Stato operativo" icon={<MapPin size={12} />} hint="Aperto / Attenzione / Chiuso — visibile a tutti">
                                <select
                                    data-testid="form-state"
                                    value={editing.state || "open"}
                                    onChange={(e) => setEditing({ ...editing, state: e.target.value })}
                                    className={inputCls}
                                >
                                    <option value="open">🟢 Aperto</option>
                                    <option value="warning">🟡 Attenzione</option>
                                    <option value="closed">🔴 Chiuso</option>
                                </select>
                            </Field>
                            <Field label="Condizioni fondo" icon={<MapPin size={12} />} hint="Verifica manuale. 'Bagnato · auto' viene impostato dal controllo pioggia ogni 3 ore">
                                <select
                                    data-testid="form-condition"
                                    value={editing.condition || "unknown"}
                                    onChange={(e) => setEditing({ ...editing, condition: e.target.value })}
                                    className={inputCls}
                                >
                                    <option value="unknown">⚪ Da verificare</option>
                                    <option value="dry_verified">🟢 Asciutto · verificato</option>
                                    <option value="wet_verified">🟠 Bagnato · verificato</option>
                                    {editing.condition === "wet_auto" && <option value="wet_auto">🟠 Bagnato · auto (impostato dai dati pioggia)</option>}
                                </select>
                            </Field>
                            <Field label="Nota stato (IT)" hint="Una riga: es. 'fondo bagnato', 'lavori in corso'…" full>
                                <input
                                    type="text"
                                    data-testid="form-state-note"
                                    value={editing.state_note || ""}
                                    onChange={(e) => setEditing({ ...editing, state_note: e.target.value })}
                                    placeholder="Es. Fondo bagnato dopo le piogge"
                                    maxLength={240}
                                    className={inputCls}
                                />
                            </Field>
                        </div>

                        <label className="flex items-center gap-3 cursor-pointer select-none pt-2 border-t border-white/10">
                            <input
                                type="checkbox"
                                data-testid="form-featured"
                                checked={!!editing.is_trail_of_month}
                                onChange={(e) => setEditing({ ...editing, is_trail_of_month: e.target.checked })}
                                className="w-4 h-4 accent-abnormal-red"
                            />
                            <span className="text-sm uppercase tracking-wider font-bold">Trail del mese (in homepage)</span>
                        </label>

                        <div className="flex justify-end gap-3 pt-3">
                            <button type="button" onClick={close} className="px-5 py-2 border border-white/20 uppercase tracking-wider text-xs font-bold hover:border-white">Annulla</button>
                            <button
                                type="submit"
                                data-testid="form-submit"
                                className="bg-abnormal-red text-white px-6 py-3 uppercase tracking-wider text-xs font-bold hover:bg-white hover:text-abnormal-red transition-colors"
                            >
                                {editing.id ? "Salva modifiche" : "Pubblica trail"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

const inputCls = "w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm";

function Field({ label, children, full = false, hint, icon }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold flex items-center gap-2 mb-1">
                {icon} {label}
            </label>
            {children}
            {hint && <p className="text-[11px] text-zinc-600 mt-1">{hint}</p>}
        </div>
    );
}
